import { useCallback, useRef } from 'react';
import type { RetryBudget } from '../../../../../common/utils/retry-budget';
import { sleep } from '../../../../../common/utils/sleep';
import { checkSourceValidity } from '../../../../../common/utils/utils';
import { MEDIA_MESSAGES } from '../constants/error-messages';
import { getErrorFromMediaError } from '../utils/get-error-from-media-error';
type PropsT = {
  getSignedUrl: (() => Promise<string | undefined>) | null;
};
type CallbackPropsT = {
  mediaError: MediaError | Error;
  currentSrc: string;
  mediaType: string;
  skipSourceValidation: boolean;
  retryBudget: RetryBudget;
};
export function useRefetchSignedUrl({
  getSignedUrl
}: PropsT) {
  const getSignedUrlRef = useRef(getSignedUrl);
  getSignedUrlRef.current = getSignedUrl;
  return useCallback(async ({
    mediaError,
    currentSrc,
    mediaType,
    skipSourceValidation,
    retryBudget
  }: CallbackPropsT): Promise<string> => {
    const isSourceAlreadyValid = skipSourceValidation ? false : await checkSourceValidity(currentSrc, mediaType);
    if (isSourceAlreadyValid) {
      throw getErrorFromMediaError(mediaError);
    }

    // SignedUrl refresh is not supported. This is in case of fractal, where
    // we show an error to use to pause task and try again.
    if (!getSignedUrlRef.current) {
      throw new Error(MEDIA_MESSAGES.SIGNED_URL_REFRESH_NOT_SUPPORTED);
    }

    // Always record a failure when we start a refetch attempt.
    retryBudget.registerFailure();
    if (!retryBudget.canAttempt()) {
      throw getErrorFromMediaError(mediaError);
    }
    while (retryBudget.canAttempt()) {
      try {
        const updatedSrc = await getSignedUrlRef.current();
        if (!updatedSrc) {
          throw new Error('Failed to refresh signed url. Returned empty url');
        }
        return updatedSrc;
      } catch (error) {
        retryBudget.registerFailure();
        if (retryBudget.exhausted) {
          throw error;
        }
        const backoff = retryBudget.getNextBackoffMs();
        await sleep(backoff);
      }
    }
    throw getErrorFromMediaError(mediaError);
  }, []);
}